import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Button } from '../../../components/button';
import { useDoc31Store } from '../../../contexts/mobx';
import { useUnsavedChanges } from '../../../contexts/unsaved-changes';
import {
    Doc31Configuration,
    Doc31ConfigurationSection,
    Doc31ConfigurationView,
    Doc31ValidationResult,
} from '../../../types/doc31';
import {
    DOC31_DEFAULT_PLAY_DAYS,
    doc31FieldId,
    doc31PlayDaysForWindow,
} from '../../../utils/doc31';
import { CatalogKind } from './catalog-section';
import { scrollToSetupField, SetupFormApi } from './fields';
import { ReadinessPanel } from './readiness-panel';
import style from './style.module.scss';

/** Saved one at a time, in this order when several are pending. */
const SECTIONS: Doc31ConfigurationSection[] = [
    'edition',
    'rules',
    'bonuses',
    'badges',
];

/** Each section's save, as screen readers name its button. */
const SAVE_LABELS: Record<Doc31ConfigurationSection, string> = {
    edition: 'Save dates and rules version',
    rules: 'Save game rules',
    bonuses: 'Save bonus actions',
    badges: 'Save badges',
};

/** Frames to wait for a lazily mounted pane to render the field. */
const SCROLL_TRIES = 10;

export type Doc31SetupTabKey = 'setup' | 'catalog';

export interface Doc31SetupFormProviderProps {
    /** The saved configuration the forms start from. */
    view: Doc31ConfigurationView;
    onOpenCoupon: (promotionId: number) => void;
    /** Brings the tab holding a field forward before scrolling to it. */
    onShowTab: (tab: Doc31SetupTabKey) => void;
    children: React.ReactNode;
}

/**
 * A section as compared for a reset: bonuses and badges by key, so a saved
 * reorder alone keeps unsaved catalog edits.
 */
function comparable(
    section: Doc31ConfigurationSection,
    value: unknown
): unknown {
    return (section === 'bonuses' || section === 'badges') &&
        Array.isArray(value)
        ? sortBy(value, 'key')
        : value;
}

interface SetupFormContextValue {
    form: SetupFormApi;
    view: Doc31ConfigurationView;
    isReadOnly: boolean;
    dirty: Record<Doc31ConfigurationSection, boolean>;
    isSaving: boolean;
    savingSection: Doc31ConfigurationSection | null;
    /** Why the API refused the last section save (422). */
    refusal: Doc31ValidationResult | null;
    panelTitle: string;
    expanded: Record<CatalogKind, string[]>;
    toggleExpanded: (kind: CatalogKind, key: string) => void;
    onPlayWindowChange: (playWindow: {
        playStartsAt: string;
        playEndsAt: string;
    }) => void;
    /** Unsaved Play days (null: cleared), for the page header. */
    onMaxPlayDaysChange: (days: number | null) => void;
    /** Resolves whether the section saved. */
    onSaveSection: (section: Doc31ConfigurationSection) => Promise<boolean>;
    onFocusField: (path: string) => void;
    onOpenCoupon: (promotionId: number) => void;
}

const SetupFormContext = createContext<SetupFormContextValue | null>(null);

/** The section forms Setup and Bonuses & badges edit. */
export function useSetupForm(): SetupFormContextValue {
    const context = useContext(SetupFormContext);
    if (!context) {
        throw new Error(
            'useSetupForm must be used within Doc31SetupFormProvider'
        );
    }
    return context;
}

/** A section's formik form: its values hold that one section by name. */
interface SectionForm {
    values: Partial<Doc31Configuration>;
    initialValues: Partial<Doc31Configuration>;
    dirty: boolean;
    setFieldValue(field: string, value: unknown): unknown;
    resetForm(next?: { values?: Partial<Doc31Configuration> }): void;
}

/** Form values for one section: `{ rules: {...} }`. */
function sectionValues<S extends Doc31ConfigurationSection>(
    section: S,
    saved: Doc31Configuration[S]
): Pick<Doc31Configuration, S> {
    return { [section]: toJS(saved) } as Pick<Doc31Configuration, S>;
}

/**
 * What a Save sends. Capabilities have no field on screen and nothing in the
 * game reads them: unset goes out as none (`[]`), so a live edition can
 * store its rules.
 */
function toSectionBody<S extends Doc31ConfigurationSection>(
    section: S,
    value: Doc31Configuration[S]
): Doc31Configuration[S] {
    if (section !== 'rules') {
        return value;
    }
    const rules = value as Doc31Configuration['rules'];
    return {
        ...rules,
        enabledCapabilities: rules.enabledCapabilities ?? [],
    } as Doc31Configuration[S];
}

function useSectionForm<S extends Doc31ConfigurationSection>(
    section: S,
    saved: Doc31Configuration[S]
) {
    const [initialValues] = useState(() => sectionValues(section, saved));
    return useFormik<Pick<Doc31Configuration, S>>({
        initialValues,
        onSubmit: () => undefined,
    });
}

type SetupFormStateProps = Doc31SetupFormProviderProps;

const SetupFormState = observer(function SetupFormState({
    view,
    onOpenCoupon,
    onShowTab,
    children,
}: SetupFormStateProps) {
    const store = useDoc31Store();
    const { isReadOnly, reloadCount } = store;

    const { configuration } = view;
    const edition = useSectionForm('edition', configuration.edition);
    const rules = useSectionForm('rules', configuration.rules);
    const bonuses = useSectionForm('bonuses', configuration.bonuses);
    const badges = useSectionForm('badges', configuration.badges);
    const forms: Record<Doc31ConfigurationSection, SectionForm> = {
        edition,
        rules,
        bonuses,
        badges,
    };
    const formsRef = useRef(forms);
    formsRef.current = forms;
    const values: Doc31Configuration = {
        edition: edition.values.edition,
        rules: rules.values.rules,
        bonuses: bonuses.values.bonuses,
        badges: badges.values.badges,
    };
    const dirty: Record<Doc31ConfigurationSection, boolean> = {
        edition: edition.dirty,
        rules: rules.dirty,
        bonuses: bonuses.dirty,
        badges: badges.dirty,
    };
    const isDirty = SECTIONS.some((section) => dirty[section]);

    const [refusal, setRefusal] = useState<{
        section: Doc31ConfigurationSection;
        validation: Doc31ValidationResult;
    } | null>(null);
    const [
        savingSection,
        setSavingSection,
    ] = useState<Doc31ConfigurationSection | null>(null);
    /** Open bonus and badge cards, by key. */
    const [expanded, setExpanded] = useState<Record<CatalogKind, string[]>>({
        bonuses: [],
        badges: [],
    });
    /** Opens the bonus and badge cards these API paths point into. */
    const openCards = useCallback(
        (paths: string[]) =>
            setExpanded((current) => {
                let next = current;
                for (const path of paths) {
                    const [section, key] = path.split('.');
                    if (
                        (section === 'bonuses' || section === 'badges') &&
                        key &&
                        !next[section].includes(key)
                    ) {
                        next = { ...next, [section]: [...next[section], key] };
                    }
                }
                return next;
            }),
        []
    );

    /** Shows a section as saved, dropping its edits and its refusal. */
    const resetSection = useCallback(
        <S extends Doc31ConfigurationSection>(
            section: S,
            saved: Doc31Configuration[S]
        ) => {
            formsRef.current[section].resetForm({
                values: sectionValues(section, saved),
            });
            setRefusal((current) =>
                current?.section === section ? null : current
            );
            if (section === 'edition') {
                store.setDraftPlayWindow(null);
            }
            if (section === 'rules') {
                store.setDraftMaxPlayDays(undefined);
            }
        },
        [store]
    );

    // A new saved configuration replaces only the sections it changed, so a
    // save elsewhere keeps unsaved edits in the others. An explicit Reload
    // (the stale banner) replaces them all.
    const shownReload = useRef(reloadCount);
    useEffect(() => {
        const isReload = shownReload.current !== reloadCount;
        shownReload.current = reloadCount;
        for (const section of SECTIONS) {
            const saved = toJS(configuration[section]);
            if (
                isReload ||
                !isEqual(
                    comparable(section, saved),
                    comparable(
                        section,
                        formsRef.current[section].initialValues[section]
                    )
                )
            ) {
                resetSection(section, saved);
            }
        }
    }, [configuration, reloadCount, resetSection]);

    useEffect(
        () => () => {
            store.setDraftPlayWindow(null);
            store.setDraftMaxPlayDays(undefined);
        },
        [store]
    );

    /**
     * Sends one section; keeps the reason it was refused (422). Resolves
     * whether it saved.
     */
    const saveSection = useCallback(
        async (section: Doc31ConfigurationSection): Promise<boolean> => {
            setSavingSection(section);
            try {
                const result = await store.saveConfigurationSection(
                    section,
                    toSectionBody(section, values[section])
                );
                if (result.ok) {
                    resetSection(section, result.data.configuration[section]);
                    return true;
                }
                setRefusal(
                    result.validation
                        ? { section, validation: result.validation }
                        : null
                );
                return false;
            } finally {
                setSavingSection(null);
            }
        },
        [store, values, resetSection]
    );

    // Leaving with edits: each edited section in turn (each save sends the
    // version the previous one returned). Play days cap the play window, so
    // a raise saves before longer dates and a cut after shorter ones: neither
    // save is refused for the other. A failure keeps the operator here.
    const onModalSave = useCallback(async () => {
        const raisesPlayDays =
            (values.rules.maxPlayDays ?? DOC31_DEFAULT_PLAY_DAYS) >
            (configuration.rules.maxPlayDays ?? DOC31_DEFAULT_PLAY_DAYS);
        const order: Doc31ConfigurationSection[] = raisesPlayDays
            ? ['rules', 'edition', 'bonuses', 'badges']
            : SECTIONS;
        for (const section of order) {
            if (dirty[section] && !(await saveSection(section))) {
                throw new Error('Setup not saved');
            }
        }
    }, [
        dirty,
        saveSection,
        values.rules.maxPlayDays,
        configuration.rules.maxPlayDays,
    ]);

    // A closed edition can't save, so its edits must not hold the operator.
    useUnsavedChanges({ dirty: isDirty && !isReadOnly, onSave: onModalSave });

    // Cancels a scroll still waiting for its field when another starts.
    const pendingScroll = useRef<number | null>(null);
    useEffect(
        () => () => {
            if (pendingScroll.current !== null) {
                window.cancelAnimationFrame(pendingScroll.current);
            }
        },
        []
    );

    const onFocusField = useCallback(
        (path: string) => {
            const section = path.split('.')[0];
            onShowTab(
                section === 'bonuses' || section === 'badges'
                    ? 'catalog'
                    : 'setup'
            );
            openCards([path]);
            // The tab may mount its pane, and the card may open, only after
            // this render: wait a few frames for the field itself.
            if (pendingScroll.current !== null) {
                window.cancelAnimationFrame(pendingScroll.current);
            }
            let triesLeft = SCROLL_TRIES;
            const attempt = () => {
                triesLeft -= 1;
                if (
                    triesLeft > 0 &&
                    !document.getElementById(doc31FieldId(path))
                ) {
                    pendingScroll.current = window.requestAnimationFrame(
                        attempt
                    );
                    return;
                }
                pendingScroll.current = null;
                scrollToSetupField(path);
            };
            pendingScroll.current = window.requestAnimationFrame(attempt);
        },
        [onShowTab, openCards]
    );

    const toggleExpanded = useCallback(
        (kind: CatalogKind, key: string) =>
            setExpanded((current) => ({
                ...current,
                [kind]: current[kind].includes(key)
                    ? current[kind].filter((expandedKey) => expandedKey !== key)
                    : [...current[kind], key],
            })),
        []
    );

    // Play days follow the dates: one door per local date of the window. An
    // operator may still type another value afterwards.
    const savedPlayDays = configuration.rules.maxPlayDays;
    const onPlayWindowChange = useCallback(
        (playWindow: { playStartsAt: string; playEndsAt: string }) => {
            store.setDraftPlayWindow(playWindow);
            const playDays = doc31PlayDaysForWindow(
                { ...playWindow, timezone: view.timezone },
                savedPlayDays
            );
            if (
                playDays === undefined ||
                playDays === formsRef.current.rules.values.rules?.maxPlayDays
            ) {
                return;
            }
            formsRef.current.rules.setFieldValue('rules.maxPlayDays', playDays);
            store.setDraftMaxPlayDays(
                playDays === savedPlayDays ? undefined : playDays
            );
        },
        [store, view.timezone, savedPlayDays]
    );

    const onMaxPlayDaysChange = useCallback(
        (days: number | null) => store.setDraftMaxPlayDays(days),
        [store]
    );

    const form: SetupFormApi = {
        values,
        timezone: view.timezone,
        readOnly: isReadOnly,
        // Formik paths start with the section, which names its form.
        setValue: (formikPath, value) =>
            forms[
                formikPath.split('.')[0] as Doc31ConfigurationSection
            ].setFieldValue(formikPath, value),
    };

    const value: SetupFormContextValue = {
        form,
        view,
        isReadOnly,
        dirty,
        isSaving: store.isSaving,
        savingSection,
        refusal: refusal?.validation ?? null,
        panelTitle:
            store.status === 'draft' || store.status === 'scheduled'
                ? 'Ready to go live?'
                : 'Setup checks',
        expanded,
        toggleExpanded,
        onPlayWindowChange,
        onMaxPlayDaysChange,
        onSaveSection: saveSection,
        onFocusField,
        onOpenCoupon,
    };

    return (
        <SetupFormContext.Provider value={value}>
            {children}
        </SetupFormContext.Provider>
    );
});

/**
 * Holds one form per configuration section (the API saves each on its own)
 * for both tabs, with one unsaved-changes guard.
 */
export const Doc31SetupFormProvider: React.FC<Doc31SetupFormProviderProps> = ({
    view,
    ...props
}) => <SetupFormState key={view.editionKey} view={view} {...props} />;

/** A section's save, on its title row: saves that section only. */
export const SectionSave = ({
    section,
}: {
    section: Doc31ConfigurationSection;
}) => {
    const {
        isReadOnly,
        dirty,
        isSaving,
        savingSection,
        onSaveSection,
    } = useSetupForm();
    if (isReadOnly) {
        return null;
    }
    return (
        <span className={style.section_save}>
            {dirty[section] && (
                <span className={style.unsaved}>Unsaved changes</span>
            )}
            <Button
                variant="primary"
                aria-label={SAVE_LABELS[section]}
                loading={savingSection === section}
                disabled={!dirty[section] || isSaving}
                onClick={() => onSaveSection(section)}
            >
                Save
            </Button>
        </span>
    );
};

/** A tab of the form: its sections on the left, the go-live check right. */
export const SetupFormPane = ({ children }: { children: React.ReactNode }) => {
    const {
        view,
        dirty,
        refusal,
        panelTitle,
        onFocusField,
        onOpenCoupon,
    } = useSetupForm();
    return (
        <div className={style.layout}>
            <div className={style.form}>{children}</div>
            <ReadinessPanel
                title={panelTitle}
                validation={view.validation}
                refusal={refusal}
                isDirty={SECTIONS.some((section) => dirty[section])}
                onFocusField={onFocusField}
                onOpenCoupon={onOpenCoupon}
            />
        </div>
    );
};
